/**
 * Waste API Client
 *
 * @remarks
 * Cliente HTTP para interactuar con los endpoints del módulo Waste.
 * Maneja autenticación automáticamente usando el token del store.
 *
 * @module Waste/API
 */

import axios, { type AxiosInstance } from 'axios';
import { useAuthStore } from '../../../auth/useAuthStore';
import type {
  WasteVehicle,
  WasteContainer,
  WasteZone,
  WasteRoute,
  WasteTrip,
  WasteDepot,
  WasteHotspot,
  GpsPosition,
  Driver,
  CollectionCrew,
  CreateVehicleDto,
  UpdateVehicleDto,
  CreateContainerDto,
  UpdateContainerDto,
  CreateZoneDto,
  UpdateZoneDto,
  CreateRouteDto,
  UpdateRouteDto,
  CreateTripDto,
  UpdateTripDto,
  CreateDepotDto,
  UpdateDepotDto,
  CreateDriverDto,
  UpdateDriverDto,
  CreateCrewDto,
  UpdateCrewDto,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

function getAuthToken(): string | null {
  const storeToken = useAuthStore.getState().token;
  if (storeToken) {
    return storeToken;
  }

  try {
    const authStore = localStorage.getItem('auth-storage');
    if (!authStore) {
      return null;
    }

    const parsed = JSON.parse(authStore) as {
      state?: { token?: string };
      token?: string;
    };

    return parsed.state?.token || parsed.token || null;
  } catch (error) {
    console.warn('[WasteAPI] Error parsing auth token:', error);
    return null;
  }
}

/**
 * Filters comunes
 */
export interface WasteFilters {
  projectId: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * Crea una instancia de axios con configuración base
 */
function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: `${API_URL}/waste`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Interceptor para agregar token automáticamente
  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Interceptor para manejar errores comunes
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('[WasteAPI] Unauthorized - token may be expired');
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

const api = createApiInstance();

/**
 * Cliente API para gestión de residuos municipales
 */
export const wasteApi = {
  /**
   * Vehicles - Vehículos recolectores
   */
  vehicles: {
    async getAll(filters: WasteFilters): Promise<WasteVehicle[]> {
      const response = await api.get<WasteVehicle[]>('/vehicles', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<WasteVehicle> {
      const response = await api.get<WasteVehicle>(`/vehicles/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateVehicleDto): Promise<WasteVehicle> {
      const response = await api.post<WasteVehicle>('/vehicles', data, {
        params: { projectId },
      });
      return response.data;
    },

    async update(id: string, data: UpdateVehicleDto): Promise<WasteVehicle> {
      const response = await api.patch<WasteVehicle>(`/vehicles/${id}`, data);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/vehicles/${id}`);
    },

    async getPositions(id: string, from?: Date, to?: Date): Promise<GpsPosition[]> {
      const response = await api.get<GpsPosition[]>(`/vehicles/${id}/positions`, {
        params: { from: from?.toISOString(), to: to?.toISOString() },
      });
      return response.data;
    },

    async getTrips(id: string): Promise<WasteTrip[]> {
      const response = await api.get<WasteTrip[]>(`/vehicles/${id}/trips`);
      return response.data;
    },
  },

  /**
   * Containers - Contenedores de residuos
   */
  containers: {
    async getAll(filters: WasteFilters): Promise<WasteContainer[]> {
      const response = await api.get<WasteContainer[]>('/containers', { params: filters });
      return response.data;
    },

    async getByProject(projectId: string): Promise<WasteContainer[]> {
      const response = await api.get<WasteContainer[]>('/containers', { params: { projectId } });
      return response.data;
    },

    async getById(id: string): Promise<WasteContainer> {
      const response = await api.get<WasteContainer>(`/containers/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateContainerDto): Promise<WasteContainer> {
      const response = await api.post<WasteContainer>('/containers', data, {
        params: { projectId },
      });
      return response.data;
    },

    async update(id: string, data: UpdateContainerDto): Promise<WasteContainer> {
      const response = await api.patch<WasteContainer>(`/containers/${id}`, data);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/containers/${id}`);
    },

    async getGeoJSON(projectId: string): Promise<GeoJSON.FeatureCollection> {
      const response = await api.get<GeoJSON.FeatureCollection>('/containers/geojson', {
        params: { projectId },
      });
      return response.data;
    },
  },

  /**
   * Zones - Zonas de cobertura
   */
  zones: {
    async getAll(filters: WasteFilters): Promise<WasteZone[]> {
      const response = await api.get<WasteZone[]>('/zones', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<WasteZone> {
      const response = await api.get<WasteZone>(`/zones/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateZoneDto): Promise<WasteZone> {
      const response = await api.post<WasteZone>('/zones', data, {
        params: { projectId },
      });
      return response.data;
    },

    async update(id: string, data: UpdateZoneDto): Promise<WasteZone> {
      const response = await api.patch<WasteZone>(`/zones/${id}`, data);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/zones/${id}`);
    },

    async getGeoJSON(projectId: string): Promise<GeoJSON.FeatureCollection> {
      const response = await api.get<GeoJSON.FeatureCollection>('/zones/geojson', {
        params: { projectId },
      });
      return response.data;
    },
  },

  /**
   * Depots - Depósitos y puntos de entrega
   */
  depots: {
    async getAll(filters: WasteFilters): Promise<WasteDepot[]> {
      const response = await api.get<WasteDepot[]>('/depots', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<WasteDepot> {
      const response = await api.get<WasteDepot>(`/depots/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateDepotDto): Promise<WasteDepot> {
      const response = await api.post<WasteDepot>('/depots', data, {
        params: { projectId },
      });
      return response.data;
    },

    async update(id: string, data: UpdateDepotDto): Promise<WasteDepot> {
      const response = await api.patch<WasteDepot>(`/depots/${id}`, data);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/depots/${id}`);
    },

    async getGeoJSON(projectId: string): Promise<GeoJSON.FeatureCollection> {
      const response = await api.get<GeoJSON.FeatureCollection>('/depots/geojson', {
        params: { projectId },
      });
      return response.data;
    },
  },

  hotspots: {
    async getAll(filters: WasteFilters): Promise<WasteHotspot[]> {
      const response = await api.get<WasteHotspot[]>('/hotspots', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<WasteHotspot> {
      const response = await api.get<WasteHotspot>(`/hotspots/${id}`);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/hotspots/${id}`);
    },

    async getGeoJSON(projectId: string): Promise<GeoJSON.FeatureCollection> {
      const response = await api.get<GeoJSON.FeatureCollection>('/hotspots/geojson', {
        params: { projectId },
      });
      return response.data;
    },
  },

  /**
   * Routes - Rutas de recolección
   */
  routes: {
    async getAll(filters: WasteFilters): Promise<WasteRoute[]> {
      const response = await api.get<WasteRoute[]>('/routes', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<WasteRoute> {
      const response = await api.get<WasteRoute>(`/routes/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateRouteDto): Promise<WasteRoute> {
      const response = await api.post<WasteRoute>('/routes', {
        ...data,
        projectId,
      });
      return response.data;
    },

    async update(id: string, data: UpdateRouteDto): Promise<WasteRoute> {
      const response = await api.patch<WasteRoute>(`/routes/${id}`, data);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/routes/${id}`);
    },

    async optimize(id: string): Promise<WasteRoute> {
      const response = await api.get<WasteRoute>(`/routes/${id}/optimize`);
      return response.data;
    },

    async getGeoJSON(projectId: string): Promise<GeoJSON.FeatureCollection> {
      const response = await api.get<GeoJSON.FeatureCollection>('/routes/geojson', {
        params: { projectId },
      });
      return response.data;
    },
  },

  /**
   * Trips - Viajes/Recorridos
   */
  trips: {
    async getAll(filters: WasteFilters): Promise<WasteTrip[]> {
      const response = await api.get<WasteTrip[]>('/trips', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<WasteTrip> {
      const response = await api.get<WasteTrip>(`/trips/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateTripDto): Promise<WasteTrip> {
      const response = await api.post<WasteTrip>('/trips', data, {
        params: { projectId },
      });
      return response.data;
    },

    async update(id: string, data: UpdateTripDto): Promise<WasteTrip> {
      const response = await api.patch<WasteTrip>(`/trips/${id}`, data);
      return response.data;
    },

    async complete(id: string): Promise<WasteTrip> {
      const response = await api.post<WasteTrip>(`/trips/${id}/complete`);
      return response.data;
    },

    async getTrack(id: string): Promise<GpsPosition[]> {
      const response = await api.get<GpsPosition[]>(`/trips/${id}/track`);
      return response.data;
    },

    async getActiveTrips(projectId: string): Promise<WasteTrip[]> {
      const response = await api.get<WasteTrip[]>('/trips', {
        params: { projectId, status: 'in_progress' },
      });
      return response.data;
    },
  },

  /**
   * Drivers - Conductores
   */
  drivers: {
    async getAll(filters: WasteFilters): Promise<Driver[]> {
      const response = await api.get<Driver[]>('/drivers', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<Driver> {
      const response = await api.get<Driver>(`/drivers/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateDriverDto): Promise<Driver> {
      const response = await api.post<Driver>('/drivers', { ...data, projectId });
      return response.data;
    },

    async update(id: string, data: UpdateDriverDto): Promise<Driver> {
      const response = await api.patch<Driver>(`/drivers/${id}`, data);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/drivers/${id}`);
    },
  },

  /**
   * Crews - Cuadrillas de recolectores
   */
  crews: {
    async getAll(filters: WasteFilters): Promise<CollectionCrew[]> {
      const response = await api.get<CollectionCrew[]>('/crews', { params: filters });
      return response.data;
    },

    async getById(id: string): Promise<CollectionCrew> {
      const response = await api.get<CollectionCrew>(`/crews/${id}`);
      return response.data;
    },

    async create(projectId: string, data: CreateCrewDto): Promise<CollectionCrew> {
      const response = await api.post<CollectionCrew>('/crews', { ...data, projectId });
      return response.data;
    },

    async update(id: string, data: UpdateCrewDto): Promise<CollectionCrew> {
      const response = await api.patch<CollectionCrew>(`/crews/${id}`, data);
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await api.delete(`/crews/${id}`);
    },
  },
};

/**
 * Helper para manejar errores de API
 */
export function getWasteApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    if (message && typeof message === 'string') {
      if (status === 401 && message.toLowerCase() === 'unauthorized') {
        return 'Tu sesión no es válida o expiró. Vuelve a iniciar sesión.';
      }
      return message;
    }

    switch (status) {
      case 400:
        return 'Los datos ingresados no son válidos';
      case 401:
        return 'No estás autenticado';
      case 403:
        return 'No tienes permisos para realizar esta acción';
      case 404:
        return 'El recurso no fue encontrado';
      case 409:
        return 'Ya existe un recurso con ese código en este proyecto';
      case 500:
        return 'Error en el servidor';
      default:
        return 'Error de conexión con el servidor';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ha ocurrido un error inesperado';
}
